import React, { useCallback, useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { generatePath, useHistory, useLocation, useParams } from 'react-router-dom';
import {
  FormCard,
  YupNumeric,
  YupNumericReq,
  YupStringReq,
  promiseModalDialog,
  toastSuccess,
  toastWarning,
} from 'motion-components';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { getRouteByName } from 'app/routes';
import { useHandleError } from 'shared-pages/services';
import { useRootStore } from 'store';
import { Card } from 'react-bootstrap';
import { FormFields } from './form-fields';
import { AddCollectionpointsButton } from './add-collectionpoints-button';
import { RecipientsGrid } from './recipients-grid';
import { SendNotificationButton } from './send-notification-button';
import { ResendNotificationButton } from './resend-notification-button';
import { CloseNotificationButton } from './close-notification-button';
import {
  gqlAddNotification,
  gqlDeleteNotification,
  gqlGetNotificationById,
  gqlUpdateNotification,
} from '../graphql';
import {
  NotificationStatus,
  NotificationTypeCodes,
  NotificationResponseTypes,
  notificationTypeOptions,
} from '../constants';
import { useTriggerMailService } from 'shared-pages/hooks/trigger-mail-service';

export const NotificationFormPage = () => {
  const { handleGraphqlError } = useHandleError('NotificationFormPage');
  const history = useHistory();
  const location: any = useLocation();
  const { appStore } = useRootStore();
  const { triggerMailService } = useTriggerMailService();
  const { id: rawId } = useParams<{ id: string }>();
  const n = Number(rawId);
  const isNew = rawId === 'new' || !Number.isFinite(n) || n <= 0;
  const idNum = isNew ? 0 : n;
  const { t } = useTranslation();
  const canEdit = appStore.isPlatformOwner;
  const [recipientsRefreshKey, setRecipientsRefreshKey] = useState(0);
  const [recipientCount, setRecipientCount] = useState(0);
  const [resendableCount, setResendableCount] = useState(0);

  const validationSchema = Yup.object().shape({
    name: YupStringReq,
    typeCode: YupStringReq,
    responseType: YupStringReq,
    tokenTtlDays: YupNumericReq.min(1, t('Πρέπει να είναι τουλάχιστον 1')),
    statusId: YupNumeric,
    notes: Yup.string().nullable(),
    isActive: Yup.boolean(),
  });

  const [createRecord] = useMutation(gqlAddNotification, {
    onError: (e) => toastWarning(e.message),
  });
  const [updateRecord] = useMutation(gqlUpdateNotification, {
    onError: (e) => toastWarning(e.message),
  });
  const [deleteRecord] = useMutation(gqlDeleteNotification, {
    onError: (e) => toastWarning(e.message),
  });

  const [get, { data: rawData, error, refetch }] = useLazyQuery(gqlGetNotificationById, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (!isNew) get({ variables: { id: idNum } });
  }, [get, idNum, isNew]);

  const data = rawData?.getNotificationById;
  const defaultType = notificationTypeOptions[0];

  // Μετά το getById (που συγχρονίζει Sent/Failed) ξαναφορτώνει το grid παραληπτών
  useEffect(() => {
    if (
      data?.id &&
      (data.statusId === NotificationStatus.Sending ||
        data.statusId === NotificationStatus.Completed)
    ) {
      setRecipientsRefreshKey((k) => k + 1);
    }
  }, [data?.id, data?.statusId]);

  const initialValues = {
    name: data?.name ?? '',
    typeCode: data?.typeCode ?? defaultType.value,
    responseType: data?.responseType ?? defaultType.responseType,
    statusId: data?.statusId ?? NotificationStatus.Draft,
    tokenTtlDays: data?.tokenTtlDays ?? 30,
    notes: data?.notes ?? '',
    isActive: data?.isActive ?? true,
  };

  // Πριν το getById το status είναι άγνωστο — θεωρούμε προσχέδιο ώστε να μην εμφανιστεί επαναποστολή
  const statusId = Number(data?.statusId ?? NotificationStatus.Draft);
  const isDraft = isNew || !data || statusId === NotificationStatus.Draft;
  const isClosed = statusId === NotificationStatus.Closed;
  const canClose =
    !isNew &&
    canEdit &&
    (statusId === NotificationStatus.Sending || statusId === NotificationStatus.Completed);
  const fieldsCanEdit = canEdit && !isClosed;

  const handleDelete = useCallback(async () => {
    const res = await promiseModalDialog({
      title: t('Διαγραφή ενημέρωσης'),
      text: t('Είστε σίγουροι ότι θέλετε να διαγράψετε την ενημέρωση;'),
    });
    if (!res) return;
    deleteRecord({ variables: { id: idNum } })
      .then(() => {
        toastSuccess(t('Επιτυχής διαγραφή'));
        history.goBack();
      })
      .catch((err) => handleGraphqlError('mutation-delete', err));
  }, [deleteRecord, handleGraphqlError, history, idNum, t]);

  const handleReturn = useCallback(() => {
    history.push(generatePath(getRouteByName('Notifications')), { ...location.state });
  }, [history, location.state]);

  const handleSubmit = async (values: typeof initialValues, actions: any) => {
    const typeOpt = notificationTypeOptions.find((o) => o.value === values.typeCode);
    const payload = {
      name: String(values.name ?? '').trim(),
      typeCode: values.typeCode || NotificationTypeCodes.BinReceived,
      responseType: typeOpt?.responseType ?? values.responseType ?? NotificationResponseTypes.YesNo,
      tokenTtlDays: Number(values.tokenTtlDays) || 30,
      notes: String(values.notes ?? '').trim() || null,
      isActive: values.isActive !== false,
      statusId: values.statusId ?? NotificationStatus.Draft,
    };

    try {
      if (isNew) {
        const { data: m } = await createRecord({ variables: { data: payload } });
        const createdId = m?.addNotification?.id;
        if (!createdId) throw new Error(t('Αποτυχία δημιουργίας'));
        toastSuccess(`${t('Επιτυχής δημιουργία')} [${createdId}]`);
        actions.setSubmitting(false);
        history.replace(generatePath(getRouteByName('Notification'), { id: createdId }), {
          ...location.state,
        });
        return;
      }

      const { data: m } = await updateRecord({
        variables: { data: { id: idNum, ...payload } },
        refetchQueries: [{ query: gqlGetNotificationById, variables: { id: idNum } }],
        awaitRefetchQueries: true,
      });
      if (!m?.updateNotification?.id) throw new Error(t('Αποτυχία ενημέρωσης'));
      toastSuccess(`${t('Επιτυχής ενημέρωση εγγραφής')} [${m.updateNotification.id}]`);
    } catch (err: any) {
      toastWarning(err?.message ?? t('Αποτυχία αποθήκευσης'));
    } finally {
      actions.setSubmitting(false);
    }
  };

  if (error) {
    return <h3 className="text-center">{error?.message}</h3>;
  }

  return (
    <>
      <FormCard
        title={`${t('Ενημέρωση')}: ${data?.name || initialValues.name || (isNew ? t('Νέα') : '-')}`}
        onSubmit={handleSubmit}
        onDelete={isNew || !canEdit || isClosed ? undefined : handleDelete}
        onReturn={handleReturn}
        permitSubmit={!fieldsCanEdit}
        permitDelete={!isNew && canEdit && !isClosed}
        noButtons={false}
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        idx={idNum}
        injectFooterElements={
          !isNew && canEdit && (isDraft || canClose)
            ? (formik) => (
                <>
                  {isDraft && (
                    <SendNotificationButton
                      notificationId={idNum}
                      disabled={formik.dirty}
                      recipientCount={recipientCount}
                      onSent={() => {
                        triggerMailService();
                        setRecipientsRefreshKey((k) => k + 1);
                        refetch();
                      }}
                    />
                  )}
                  {canClose && (
                    <>
                      <ResendNotificationButton
                        notificationId={idNum}
                        disabled={formik.dirty || resendableCount <= 0}
                        noTargets={resendableCount <= 0}
                        onResent={() => {
                          setRecipientsRefreshKey((k) => k + 1);
                          refetch();
                        }}
                      />
                      <CloseNotificationButton
                        notificationId={idNum}
                        disabled={formik.dirty}
                        onClosed={() => {
                          setRecipientsRefreshKey((k) => k + 1);
                          refetch();
                        }}
                      />
                    </>
                  )}
                </>
              )
            : undefined
        }
      >
        <FormFields canEdit={fieldsCanEdit} isNew={isNew} />
      </FormCard>

      {!isNew && (
        <Card className="shadow mt-3">
          <Card.Body>
            <RecipientsGrid
              notificationId={idNum}
              canRemove={isDraft && canEdit}
              headerActions={
                isDraft && canEdit ? (
                  <AddCollectionpointsButton
                    notificationId={idNum}
                    onAdded={() => setRecipientsRefreshKey((k) => k + 1)}
                  />
                ) : undefined
              }
              canResend={!isDraft && !isClosed && canEdit}
              refreshKey={recipientsRefreshKey}
              onCountChange={setRecipientCount}
              onResendableCountChange={setResendableCount}
              onResent={() => {
                setRecipientsRefreshKey((k) => k + 1);
                refetch();
              }}
            />
          </Card.Body>
        </Card>
      )}
    </>
  );
};
